import { useState, useCallback, useRef, useEffect } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { useEntityIndex } from '../hooks/use-entity-index'
import { useDataValidation, getEntityMessages, getFieldMessages } from '../hooks/use-data-validation'
import { commandBus } from '@/features/chat'
import { toast } from '@/store/toast-store'
import { ConfirmDelete } from './confirm-delete'
import { cn } from '@/lib/utils'
import { Trash2, AlertTriangle, AlertCircle } from 'lucide-react'
import { SchemaTreeEditor } from './schema-editor/schema-tree-editor'
import type { SchemaField } from './schema-editor/types'
import { cascadeBindingRename } from '../services/binding-cascade'
import { AskCogoSurfaceButton } from '@/shell/ide-v2/components/ask-cogo-surface-button'
import { useStudioLocale } from '@/i18n'
import type { StudioDictionary } from '@/i18n'

type InspectorCopy = StudioDictionary['dataInspector']

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseEntityId(id: string): { blockName: string; category: string; name: string } | null {
  const parts = id.split('.')
  if (parts.length < 3) return null
  return { blockName: parts[0], category: parts[1], name: parts.slice(2).join('.') }
}

/** Debounce hook — returns a stable callback that delays invocation by `delay` ms. */
function useDebouncedCallback<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const fnRef = useRef(fn)
  fnRef.current = fn
  return useCallback((...args: any[]) => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => fnRef.current(...args), delay)
  }, [delay]) as unknown as T
}

/**
 * Stable test id for a field row.
 *
 * Derived from a LOCALE-INDEPENDENT key, never from the rendered label. The
 * labels are translated now, and `e2e/studio/studio-core-editor-task-success.spec.ts`
 * drives this panel through `getByTestId('data-field-endpoint')`. Deriving the
 * id from a ko label would strip every non-ASCII character and collapse every
 * row's id to the same `data-field-` — the panel would still LOOK right and
 * the e2e would stop being able to find anything. The keys below reproduce the
 * ids the English labels used to produce, byte for byte.
 */
function fieldTestId(fieldKey: string): string {
  return `data-field-${fieldKey.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
}

// ---------------------------------------------------------------------------
// Editable Field
// ---------------------------------------------------------------------------

function EditableField({
  fieldKey,
  label,
  value,
  onChange,
  mono = false,
  placeholder,
  messages,
}: {
  fieldKey: string
  label: string
  value: string
  onChange: (v: string) => void
  mono?: boolean
  placeholder?: string
  messages?: { level: 'error' | 'warning'; message: string }[]
}) {
  const hasError = messages?.some((m) => m.level === 'error')
  const hasWarning = !hasError && messages?.some((m) => m.level === 'warning')

  return (
    <div className="py-1">
      <div className="flex items-start gap-2">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider w-20 shrink-0 pt-1.5">{label}</span>
        <input
          data-testid={fieldTestId(fieldKey)}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(
            'flex-1 h-7 rounded border bg-background px-2 text-xs focus:outline-none focus:ring-1',
            mono && 'font-mono',
            hasError
              ? 'border-destructive focus:ring-destructive text-destructive'
              : hasWarning
                ? 'border-yellow-500/50 focus:ring-yellow-500'
                : 'border-border focus:ring-ring',
          )}
        />
      </div>
      {messages?.map((m, i) => (
        <div key={i} className={cn('flex items-center gap-1 ml-[88px] mt-0.5', m.level === 'error' ? 'text-destructive' : 'text-yellow-600')}>
          {m.level === 'error' ? <AlertCircle size={10} /> : <AlertTriangle size={10} />}
          <span className="text-[10px]">{m.message}</span>
        </div>
      ))}
    </div>
  )
}

function SelectField({
  fieldKey,
  label,
  value,
  options,
  onChange,
}: {
  fieldKey: string
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className="flex items-start gap-2 py-1">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wider w-20 shrink-0 pt-1.5">{label}</span>
      <select
        data-testid={fieldTestId(fieldKey)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

function ReadOnlyField({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start gap-2 py-1">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wider w-20 shrink-0 pt-0.5">{label}</span>
      <span className={cn('text-xs break-all', mono && 'font-mono')}>{value}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Variable Detail (Editable)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// SCHEMA VOCABULARY — deliberately NOT localized.
//
// These three tables are the Cogo schema's own nouns, not product prose. An
// operator reads `"type": "string"` / `"scope": "state"` / `"type": "rest"` in
// the JSON and matches it against what this select shows; a translated label
// would break that match rather than improve the reading. HTTP methods
// (GET/POST/…) below are protocol vocabulary for the same reason.
//
// `__tests__/data-inspector-i18n.test.tsx` renders the panel under `ko` and
// asserts these option labels verbatim, so a later localization sweep cannot
// translate them silently.
// ---------------------------------------------------------------------------

const VARIABLE_TYPES = [
  { value: 'string', label: 'string' },
  { value: 'number', label: 'number' },
  { value: 'boolean', label: 'boolean' },
  { value: 'array', label: 'array' },
  { value: 'object', label: 'object' },
]

const VARIABLE_SCOPES = [
  { value: 'state', label: 'state' },
  { value: 'global', label: 'global' },
  { value: 'page', label: 'page' },
  { value: 'local', label: 'local' },
]

const DATASOURCE_TYPE_OPTIONS = [
  { value: 'rest', label: 'REST' },
  { value: 'graphql', label: 'GraphQL' },
  { value: 'static', label: 'Static' },
  { value: 'fetch', label: 'Fetch' },
  { value: 'import', label: 'Import' },
  { value: 'api-client', label: 'API client' },
  { value: 'webmcp', label: 'WebMCP' },
]

function getDatasourceType(source: Record<string, unknown>): string {
  return String(source.type ?? source.kind ?? 'rest')
}

function getDatasourceTypePatch(source: Record<string, unknown>, value: string): Record<string, unknown> {
  return source.kind != null && source.type == null
    ? { kind: value }
    : { type: value }
}

function VariableDetail({ blockName, name, entityId, validationMessages }: {
  blockName: string; name: string; entityId: string
  validationMessages: { level: 'error' | 'warning'; message: string }[]
}) {
  const project = useStudioStore((s) => s.currentProject)
  const updateVariable = useStudioStore((s) => s.updateVariable)
  const removeVariable = useStudioStore((s) => s.removeVariable)
  const selectEntity = useStudioStore((s) => s.selectEntity)
  const copy = useStudioLocale().strings.dataInspector

  const block = project?.blocks?.[blockName]
  const vars = block?.state as unknown as Record<string, Record<string, unknown>> | undefined

  // Find variable and its scope
  let varDef: Record<string, unknown> | null = null
  let scope = ''
  if (vars) {
    for (const [s, scopeVars] of Object.entries(vars)) {
      if (scopeVars && typeof scopeVars === 'object' && name in (scopeVars as object)) {
        const v = (scopeVars as Record<string, unknown>)[name]
        varDef = typeof v === 'object' && v !== null ? v as Record<string, unknown> : {}
        scope = s
        break
      }
    }
  }

  const [deleteConfirm, setDeleteConfirm] = useState(false)

  const debouncedUpdate = useDebouncedCallback((field: string, value: unknown) => {
    if (!scope) return
    updateVariable(blockName, scope, name, { [field]: value })
  }, 500)

  if (!varDef) return <p className="text-xs text-muted-foreground p-3">{copy.emptyStates.variableNotFound}</p>

  const nameMessages = getFieldMessages(validationMessages as any, entityId, 'name')
    .map((m) => ({ level: m.level, message: m.message }))
  const defaultMessages = getFieldMessages(validationMessages as any, entityId, 'defaultValue')
    .map((m) => ({ level: m.level, message: m.message }))
  const exprMessages = getFieldMessages(validationMessages as any, entityId, 'expression')
    .map((m) => ({ level: m.level, message: m.message }))

  // Entity-level warnings (no specific field)
  const entityWarnings = validationMessages.filter((m) => !('field' in m) || !(m as any).field)

  function handleDelete() {
    removeVariable(blockName, scope, name)
    selectEntity(null)
    toast.success(copy.toasts.variableDeleted(name))
  }

  // Check cross-references
  const projectJson = JSON.stringify(project ?? {})
  const isReferenced = projectJson.includes(`#{${name}}`) || projectJson.includes(`#{${name}.`)

  return (
    <div className="px-3 py-2 space-y-1">
      <ReadOnlyField label={copy.fields.name} value={<span className="font-mono">{name}</span>} />
      <ReadOnlyField label={copy.fields.block} value={blockName} />
      <SelectField
        fieldKey="scope"
        label={copy.fields.scope}
        value={scope}
        options={VARIABLE_SCOPES}
        onChange={(newScope) => {
          // Move variable to new scope: remove from old, add to new
          removeVariable(blockName, scope, name)
          useStudioStore.getState().addVariable(blockName, newScope, name, varDef as any)
        }}
      />
      <SelectField
        fieldKey="type"
        label={copy.fields.type}
        value={String(varDef.type ?? 'string')}
        options={VARIABLE_TYPES}
        onChange={(v) => debouncedUpdate('type', v)}
      />
      <EditableField
        fieldKey="default"
        label={copy.fields.defaultValue}
        value={varDef.default !== undefined ? String(varDef.default) : ''}
        onChange={(v) => debouncedUpdate('default', v)}
        mono
        placeholder={copy.placeholders.noDefault}
        messages={defaultMessages.length > 0 ? defaultMessages : undefined}
      />
      {/*
        The Expression ROW stays conditional — a plain variable should not
        show a field it does not have. The DOOR to the Expression Lab does
        not: it used to live inside this condition, so on a generated
        project — whose variables are plain state with no `expression` — the
        Lab had no entrance anywhere in the product that produced them. The
        tool for AUTHORING an expression was reachable only from a variable
        that already had one.
      */}
      <div className="space-y-1">
        {(varDef.expression != null || exprMessages.length > 0) && (
          <EditableField
            fieldKey="expression"
            label={copy.fields.expression}
            value={String(varDef.expression ?? '')}
            onChange={(v) => debouncedUpdate('expression', v)}
            mono
            placeholder={copy.placeholders.expression}
            messages={exprMessages.length > 0 ? exprMessages : undefined}
          />
        )}
        <button
          type="button"
          data-testid="open-expression-lab"
          onClick={() =>
            useStudioStore.getState().openExpressionLab({
              expression: String(varDef.expression ?? ''),
              applyTarget: { blockName, scope, name },
              source: 'inspector',
            })
          }
          className="text-[10px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          {varDef.expression != null
            ? copy.actions.testInExpressionLab
            : copy.actions.writeExpressionInLab}
        </button>
      </div>

      {entityWarnings.map((m, i) => (
        <div key={i} className="flex items-center gap-1 mt-1 text-yellow-600">
          <AlertTriangle size={10} />
          <span className="text-[10px]">{m.message}</span>
        </div>
      ))}

      {/* Delete */}
      {deleteConfirm ? (
        <ConfirmDelete
          entityName={name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm(false)}
          warning={isReferenced ? copy.referencedWarning : undefined}
        />
      ) : (
        <div className="pt-2">
          <button
            onClick={() => setDeleteConfirm(true)}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 size={10} />
            {copy.actions.deleteVariable}
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Datasource Detail (Editable)
// ---------------------------------------------------------------------------

function DatasourceDetail({ blockName, name, entityId, validationMessages }: {
  blockName: string; name: string; entityId: string
  validationMessages: { level: 'error' | 'warning'; message: string }[]
}) {
  const project = useStudioStore((s) => s.currentProject)
  const updateDatasource = useStudioStore((s) => s.updateDatasource)
  const removeDatasource = useStudioStore((s) => s.removeDatasource)
  const selectEntity = useStudioStore((s) => s.selectEntity)
  const entityIndex = useEntityIndex()
  const copy = useStudioLocale().strings.dataInspector

  const isAppDatasource = blockName === 'app'
  const block = isAppDatasource ? undefined : project?.blocks?.[blockName]
  const appDatasources =
    project?.manifest?.datasources as Record<string, Record<string, unknown>> | undefined
  const source = (
    isAppDatasource
      ? appDatasources?.[name]
      : (block?.data as Record<string, unknown> | undefined)?.[name]
  ) as Record<string, unknown> | undefined

  const [deleteConfirm, setDeleteConfirm] = useState(false)

  function updateField(field: string, value: unknown) {
    if (isAppDatasource) return
    updateDatasource(blockName, name, { [field]: value })
  }

  if (!source) return <p className="text-xs text-muted-foreground p-3">{copy.emptyStates.datasourceNotFound}</p>

  const endpointMessages = getFieldMessages(validationMessages as any, entityId, 'endpoint')
    .map((m) => ({ level: m.level, message: m.message }))

  function handleResponseFieldRename(oldName: string, newName: string) {
    if (!project?.blocks || oldName === newName) return

    // The resultTarget is the state variable that this datasource's response populates.
    // Convention: datasource name is the resultTarget.
    const resultTarget = String((source as any)?.resultTarget ?? name)

    const oldPath = `${resultTarget}.${oldName}`
    const newPath = `${resultTarget}.${newName}`

    try {
      const { count, updatedBlocks } = cascadeBindingRename(
        project.blocks as Record<string, unknown>,
        oldPath,
        newPath,
      )

      if (count > 0) {
        useStudioStore.setState((s) => {
          if (s.currentProject) {
            s.currentProject.blocks = updatedBlocks as typeof s.currentProject.blocks
            s.isDirty = true
          }
        })
        toast.success(copy.toasts.bindingsUpdated(count, oldPath, newPath))
      }
    } catch {
      toast.error(copy.toasts.bindingsUpdateFailed)
    }
  }

  function handleDelete() {
    if (isAppDatasource) return
    removeDatasource(blockName, name)
    selectEntity(null)
    toast.success(copy.toasts.datasourceDeleted(name))
  }

  if (isAppDatasource) {
    return (
      <div className="px-3 py-2 space-y-1">
        <ReadOnlyField label={copy.fields.name} value={<span className="font-mono">{name}</span>} />
        <ReadOnlyField label={copy.fields.scope} value={copy.appManifest.scopeValue} />
        <ReadOnlyField label={copy.fields.type} value={getDatasourceType(source)} />
        <ReadOnlyField
          label={copy.fields.path}
          value={String(source.path ?? source.endpoint ?? source.url ?? '—')}
          mono
        />
        {source.importSource != null && (
          <ReadOnlyField label={copy.fields.importSource} value={String(source.importSource)} mono />
        )}
        {source.call != null && (
          <ReadOnlyField label={copy.fields.call} value={String(source.call)} mono />
        )}
        {source.method != null && (
          <ReadOnlyField label={copy.fields.method} value={String(source.method)} />
        )}
        <div className="mt-3 rounded-md border border-border bg-muted/30 px-2.5 py-2 text-[11px] text-muted-foreground">
          {copy.appManifest.note}
        </div>
      </div>
    )
  }

  return (
    <div className="px-3 py-2 space-y-1">
      <ReadOnlyField label={copy.fields.name} value={<span className="font-mono">{name}</span>} />
      <ReadOnlyField label={copy.fields.block} value={blockName} />
      <SelectField
        fieldKey="type"
        label={copy.fields.type}
        value={getDatasourceType(source)}
        options={DATASOURCE_TYPE_OPTIONS}
        onChange={(v) => {
          const patch = getDatasourceTypePatch(source, v)
          updateField(Object.keys(patch)[0]!, Object.values(patch)[0])
        }}
      />
      {source.kind != null && source.type == null && (
        <ReadOnlyField label={copy.fields.kind} value={String(source.kind)} />
      )}
      <EditableField
        fieldKey="endpoint"
        label={copy.fields.endpoint}
        value={String(source.endpoint ?? source.url ?? '')}
        onChange={(v) => updateField('endpoint', v)}
        mono
        // Example URL, not prose — stays verbatim in every locale.
        placeholder="https://api.example.com/data"
        messages={endpointMessages.length > 0 ? endpointMessages : undefined}
      />
      <EditableField
        fieldKey="result-target"
        label={copy.fields.resultTarget}
        value={String(source.resultTarget ?? '')}
        onChange={(v) => updateField('resultTarget', v)}
        mono
        placeholder={copy.placeholders.resultTarget}
      />
      <SelectField
        fieldKey="method"
        label={copy.fields.method}
        value={String(source.method ?? 'GET')}
        options={['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => ({ value: m, label: m }))}
        onChange={(v) => updateField('method', v)}
      />
      {source.headers != null && (
        <EditableField
          fieldKey="headers"
          label={copy.fields.headers}
          value={typeof source.headers === 'string' ? source.headers : JSON.stringify(source.headers)}
          onChange={(v) => {
            try { updateField('headers', JSON.parse(v)) } catch { updateField('headers', v) }
          }}
          mono
          // JSON example, not prose — stays verbatim in every locale.
          placeholder='{"Authorization": "Bearer ..."}'
        />
      )}
      {source.body != null && (
        <EditableField
          fieldKey="body"
          label={copy.fields.body}
          value={typeof source.body === 'string' ? source.body : JSON.stringify(source.body)}
          onChange={(v) => {
            try { updateField('body', JSON.parse(v)) } catch { updateField('body', v) }
          }}
          mono
          placeholder={copy.placeholders.body}
        />
      )}
      {source.importSource != null && (
        <ReadOnlyField label={copy.fields.importSource} value={String(source.importSource)} mono />
      )}
      {source.call != null && (
        <ReadOnlyField label={copy.fields.call} value={String(source.call)} mono />
      )}
      {source.source != null && (
        <ReadOnlyField
          label={copy.fields.source}
          value={typeof source.source === 'string' ? source.source : JSON.stringify(source.source)}
          mono
        />
      )}

      {/* Request Schema */}
      <div className="pt-4 border-t border-border">
        <SchemaTreeEditor
          title={copy.schema.request}
          fields={(source.requestSchema as SchemaField[] | undefined) ?? []}
          onUpdate={(fields) => updateDatasource(blockName, name, { requestSchema: fields })}
          entityIndex={entityIndex}
        />
      </div>

      {/* Response Schema — per D-01, D-07, D-10 */}
      <div className="pt-4 border-t border-border">
        <SchemaTreeEditor
          title={copy.schema.response}
          fields={(source.responseSchema as SchemaField[] | undefined) ?? []}
          onUpdate={(fields) => updateDatasource(blockName, name, { responseSchema: fields })}
          onFieldRename={handleResponseFieldRename}
          entityIndex={entityIndex}
        />
      </div>

      {deleteConfirm ? (
        <ConfirmDelete
          entityName={name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm(false)}
        />
      ) : (
        <div className="pt-2">
          <button
            data-testid="delete-datasource-button"
            onClick={() => setDeleteConfirm(true)}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 size={10} />
            {copy.actions.deleteDatasource}
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Event Detail (Editable)
// ---------------------------------------------------------------------------

function EventDetail({ blockName, name, entityId, validationMessages }: {
  blockName: string; name: string; entityId: string
  validationMessages: { level: 'error' | 'warning'; message: string }[]
}) {
  const project = useStudioStore((s) => s.currentProject)
  const removeEventBinding = useStudioStore((s) => s.removeEventBinding)
  const addEventBinding = useStudioStore((s) => s.addEventBinding)
  const selectEntity = useStudioStore((s) => s.selectEntity)
  const index = useEntityIndex()
  const copy = useStudioLocale().strings.dataInspector

  const block = project?.blocks?.[blockName]
  const binding = (block?.events as any)?.bindings?.[name] as Record<string, unknown> | undefined

  const [deleteConfirm, setDeleteConfirm] = useState(false)

  if (!binding) return <p className="text-xs text-muted-foreground p-3">{copy.emptyStates.eventNotFound}</p>

  const parts = name.split(':')
  const flowMessages = getFieldMessages(validationMessages as any, entityId, 'flow')
    .map((m) => ({ level: m.level, message: m.message }))

  function handleFlowChange(newFlow: string) {
    // Re-create binding with updated flow
    addEventBinding(blockName, name, {
      flow: newFlow || undefined,
      action: binding!.action as string | undefined,
      params: binding!.params as Record<string, unknown> | undefined,
    })
  }

  function handleDelete() {
    removeEventBinding(blockName, name)
    selectEntity(null)
    toast.success(copy.toasts.eventRemoved(name))
  }

  return (
    <div className="px-3 py-2 space-y-1">
      <ReadOnlyField label={copy.fields.eventKey} value={<span className="font-mono">{name}</span>} />
      <ReadOnlyField label={copy.fields.block} value={blockName} />
      <ReadOnlyField label={copy.fields.component} value={<span className="font-mono">{parts[0] ?? name}</span>} />
      {/* The event NAME ('tap', 'change', ...) is schema vocabulary - verbatim. */}
      <ReadOnlyField label={copy.fields.event} value={parts[1] ?? 'tap'} />

      <div className="py-1">
        <div className="flex items-start gap-2">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider w-20 shrink-0 pt-1.5">{copy.fields.flow}</span>
          <select
            value={String(binding.flow ?? '')}
            onChange={(e) => handleFlowChange(e.target.value)}
            className={cn(
              'flex-1 h-7 rounded border bg-background px-2 text-xs font-mono focus:outline-none focus:ring-1',
              flowMessages.some((m) => m.level === 'error')
                ? 'border-destructive focus:ring-destructive'
                : 'border-border focus:ring-ring',
            )}
          >
            <option value="">{copy.flowUnbound}</option>
            {index.flows.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
        </div>
        {flowMessages.map((m, i) => (
          <div key={i} className="flex items-center gap-1 ml-[88px] mt-0.5 text-destructive">
            <AlertCircle size={10} />
            <span className="text-[10px]">{m.message}</span>
          </div>
        ))}
      </div>

      {binding.flow != null && (
        <div className="pt-1">
          <button
            className="text-xs text-primary hover:underline font-mono"
            onClick={() =>
              commandBus.dispatch({
                type: 'navigate',
                payload: { mode: 'flow', flowName: String(binding.flow) },
              })
            }
          >
            {copy.actions.openInFlowEditor}
          </button>
        </div>
      )}

      {deleteConfirm ? (
        <ConfirmDelete
          entityName={name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm(false)}
        />
      ) : (
        <div className="pt-2">
          <button
            onClick={() => setDeleteConfirm(true)}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 size={10} />
            {copy.actions.deleteEvent}
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Flow Detail (read-only, flows are edited in Flow Editor)
// ---------------------------------------------------------------------------

function FlowDetail({ blockName, name }: { blockName: string; name: string }) {
  const project = useStudioStore((s) => s.currentProject)
  const copy = useStudioLocale().strings.dataInspector
  const blockFlowRoot = (project?.blocks?.[blockName] as { flow?: unknown } | undefined)?.flow as
    | Record<string, unknown>
    | undefined
  const blockFlowMap = (blockFlowRoot?.flows ?? blockFlowRoot) as
    | Record<string, Record<string, unknown>>
    | undefined
  const flow = blockName === 'global'
    ? (project?.flows as unknown as Record<string, Record<string, unknown>> | undefined)?.[name]
    : blockFlowMap?.[name]
  if (!flow) return <p className="text-xs text-muted-foreground p-3">{copy.emptyStates.flowNotFound}</p>

  const steps = Array.isArray(flow.steps) ? flow.steps : []

  return (
    <div className="px-3 py-2 space-y-1">
      <ReadOnlyField label={copy.fields.name} value={<span className="font-mono">{name}</span>} />
      {/* The trigger NAME ('event', 'mount', ...) is schema vocabulary - verbatim. */}
      <ReadOnlyField label={copy.fields.trigger} value={String(flow.trigger ?? 'event')} />
      <ReadOnlyField label={copy.fields.steps} value={copy.stepCount(steps.length)} />
      <div className="pt-2">
        <button
          className="text-xs text-primary hover:underline"
          onClick={() =>
            commandBus.dispatch({
              type: 'navigate',
              payload: { mode: 'flow', flowName: name },
            })
          }
        >
          {copy.actions.openInFlowEditor}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// DataInspector
// ---------------------------------------------------------------------------

export function DataInspector({ onRequestChat }: { onRequestChat?: () => void } = {}) {
  const selectedEntityId = useStudioStore((s) => s.selectedEntityId)
  const project = useStudioStore((s) => s.currentProject)
  const index = useEntityIndex()
  const validationMessages = useDataValidation(index, project)
  const copy = useStudioLocale().strings.dataInspector

  if (!selectedEntityId) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-4 text-center">
        {copy.emptyStates.selectEntity}
      </div>
    )
  }

  const parsed = parseEntityId(selectedEntityId)
  if (!parsed || !project) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-4 text-center">
        {copy.emptyStates.entityNotFound}
      </div>
    )
  }

  const entityMessages = getEntityMessages(validationMessages, selectedEntityId)

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0">
          <h3 className="text-xs font-semibold">{copy.header.title}</h3>
          <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
            {selectedEntityId}
          </p>
        </div>
        <AskCogoSurfaceButton
          label={copy.header.reviewEntity}
          className="h-7 px-2"
          operationId="data.surface.review"
          operationKind="data-source"
          chatContextPatch={{
            surfaceId: 'data',
            selectedPropertyKey: selectedEntityId,
            ...(parsed.category === 'datasources'
              ? { dataSourceName: parsed.name }
              : {}),
          }}
          context={[
            'Studio data entity detail review.',
            `Entity=${selectedEntityId}`,
            `Category=${parsed.category}`,
            `Block=${parsed.blockName}`,
          ].join('; ')}
          onBeforeOpen={onRequestChat}
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        {parsed.category === 'variables' && (
          <VariableDetail
            blockName={parsed.blockName}
            name={parsed.name}
            entityId={selectedEntityId}
            validationMessages={entityMessages}
          />
        )}
        {parsed.category === 'datasources' && (
          <DatasourceDetail
            blockName={parsed.blockName}
            name={parsed.name}
            entityId={selectedEntityId}
            validationMessages={entityMessages}
          />
        )}
        {parsed.category === 'events' && (
          <EventDetail
            blockName={parsed.blockName}
            name={parsed.name}
            entityId={selectedEntityId}
            validationMessages={entityMessages}
          />
        )}
        {parsed.category === 'flows' && (
          <FlowDetail blockName={parsed.blockName} name={parsed.name} />
        )}
      </div>
      {/* Validation summary footer */}
      {entityMessages.length > 0 && (
        <div className="px-3 py-1.5 border-t border-border text-[10px]">
          <span className={cn(
            entityMessages.some((m) => m.level === 'error') ? 'text-destructive' : 'text-yellow-600',
          )}>
            {copy.validationSummary(
              entityMessages.filter((m) => m.level === 'error').length,
              entityMessages.filter((m) => m.level === 'warning').length,
            )}
          </span>
        </div>
      )}
    </div>
  )
}
